"use client";

import TheaterComedyIcon from "@mui/icons-material/TheaterComedy";
import { AppBar, Avatar, Button, Chip, Icon } from "@mui/material";
import { useStore } from "@nanostores/react";
import AvatarMenu from "common/components/AvatarMenu";
import { user } from "common/stores/UserStore";
import { Endorsement } from "common/utils/UserUtils";
import AuthClient from "common/clients/AuthClient";
import Image from "next/legacy/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import logo from "public/images/OPT-Primary-White.svg";
import React, { Suspense, useState } from "react";

export interface NavItem {
	id: number;
	label: string;
	path: string;
	icon?: React.ReactElement<typeof Icon>;
}

const baseNavItems: NavItem[] = [];

export interface OpteraAppBarProps {
	items: Array<NavItem>;
}

const OPTERA_CLASSIC_ENDORSEMENTS: Endorsement[] = [
	"sam:brand",
	"supplychain:brand",
	"supplychain:supplier",
];

/**
 * A Navigation header for Optera themed pages.
 */
function OpteraAppBar({ items = [] }: OpteraAppBarProps) {
	const $user = useStore(user);
	const pathname = usePathname();

	const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

	let navItems = [...baseNavItems, ...items];

	const handleAvatarClick = (event: React.MouseEvent<HTMLElement>) => {
		setAnchorEl(event.currentTarget);
	};

	const handleStopImpersonation = async () => {
		try {
			await AuthClient.stopImpersonation();
			window.location.href = "/admin/users";
		} catch (error) {
			console.error("Failed to stop impersonation:", error);
			window.location.href = "/";
		}
	};

	const alreadyInOpteraClassic =
		pathname != null &&
		pathname.startsWith("/legacyOverview") &&
		!pathname.startsWith("/operator/") &&
		!pathname.startsWith("/supplyChainManager");
	const showOpteraClassicAccess =
		!alreadyInOpteraClassic &&
		OPTERA_CLASSIC_ENDORSEMENTS.some((el) => $user.endorsements.includes(el));

	return (
		<AppBar component="nav" position="static" id="appBar-nav" elevation={0}>
			<div className="logo">
				<Link href="/" prefetch={false}>
					<Image src={logo} width="112" height="24" alt="Optera Logo" />
				</Link>
			</div>
			{navItems.length > 0 ? (
				<div
					role="navigation"
					aria-label="Main navigation"
					id="navItems-container"
				>
					{navItems.map((item) => (
						<Link key={item.id} href={item.path}>
							<Button className="navItem" aria-label={item.label}>
								{item.label}
							</Button>
						</Link>
					))}
				</div>
			) : (
				<div className="flex-2"></div>
			)}
			<Suspense fallback={<div>loading...</div>}>
				<div className="flex-row ai-c gap-2" suppressHydrationWarning>
					{showOpteraClassicAccess && (
						<Link
							href="/legacyOverview"
							aria-label="Open Classic Optera"
							className="switchButton"
							prefetch={false}
						>
							<Button
								tabIndex={-1}
								color="inherit"
								variant="outlined"
								size="large"
							>
								Open Classic Optera
							</Button>
						</Link>
					)}
					{$user.impersonator_username && (
						<Chip
							icon={<TheaterComedyIcon />}
							label={`Impersonating ${$user.username}`}
							title={`Original User: ${$user.impersonator_username}`}
							onDelete={handleStopImpersonation}
							color="warning"
							size="medium"
						/>
					)}
					<Avatar
						src={$user.picture}
						data-testid="Avatar"
						onClick={handleAvatarClick}
						tabIndex={0}
						sx={{
							backgroundColor: "var(--oc-palette-secondary-500)",
							fontSize: "24px",
						}}
						alt={$user.username}
						role="button"
						aria-label="User menu"
						className="avatarIcon"
					>
						{$user.username[0].toUpperCase()}
					</Avatar>
				</div>
				{$user.organization_id != 0 && (
					<AvatarMenu
						anchorEl={anchorEl}
						setAnchorEl={setAnchorEl}
						organization={$user.organization_id.toString()}
						aria-label="User menu options"
					/>
				)}
			</Suspense>
		</AppBar>
	);
}

export default OpteraAppBar;
